import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { EncounterService } from 'app/core/http/services/encounter.service';
import { OrganizationService } from 'app/core/http/services/organization.service';
import { PractitionerService } from 'app/core/http/services/practitioner.service';
import { PractitionerOrganizationAffiliationService } from 'app/core/http/services/practititoner-organization-affiliation.service';
import { Encounter } from 'app/core/interfaces/encounter';
import { HealthcareserviceSpecialists } from 'app/core/interfaces/healthcareservice';
import { OrganizationInterface } from 'app/core/interfaces/organization';
import { Practitioner } from 'app/core/interfaces/practitioner';
import { debounceTime, finalize, Subject, take, takeUntil, timeout } from 'rxjs';
import { UtilsService } from '../../utils.service';


import dayjs from 'app/core/utils/dayjs-config';

@Component({
  selector: 'schedule-specialist',
  styleUrls: ['schedule-specialist.component.scss'],
  templateUrl: 'schedule-specialist.component.html',
})
export class ScheduleSpecialistComponent implements OnChanges {
  @Input() healthcareservice!: HealthcareserviceSpecialists;
  @Input() organization_id!: string;
  @Input() practitioner_id!: string;
  @Input() initialSlotsCount = 5;
  @Output() onSelectedDatetime = new EventEmitter<any>();

  currentDate = dayjs().toDate();
  selectedSlot: { date: string; hour: string } | null = null;
  practitionerHours: any[] = [];
  availability: Record<string, string[]> = {};
  dates: { day: string; date: string; fullDate: string }[] = [];
  expanded = false;
  animationDirection: 'left' | 'right' | null = null;
  offset = 0;
  encountersDone: Record<string, string[]> = {};
  encounterPractitioner: Encounter[] = [];
  isAnimating = false;
  blockedDates: string[] = [];
  practitioner!: Practitioner;
  organization!: OrganizationInterface;
  isByOrder = false;
  averageDuration: number = 30;
  private destroy$ = new Subject<void>();
  public loading: boolean = true
  public hoursLoadMessage: string | null = null;
  public canRetryHoursLoad = false;

  constructor(
    private affiliationOrganizationPractitioner: PractitionerOrganizationAffiliationService,
    private encounterService: EncounterService,
    private practitionerService: PractitionerService,
    private organizationService: OrganizationService,
    private readonly utilsService: UtilsService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['healthcareservice'] && this.healthcareservice) {
      this.getPractitionerHours();

      if (this.healthcareservice.practitioner_id) {
        this.getOrganization();
        this.getPractitioner();
        this.loadData();
      }
    }
  }

  ngOnInit(): void {
    this.generateWeek(this.currentDate);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get hasHourAvailable(): boolean {
    return this.practitionerHours && this.practitionerHours.length > 0;
  }

  get hasAvailableTimeInVisibleDates(): boolean {
    return this.dates.some(({ fullDate }) => {
      const slots = this.availability[fullDate] ?? [];
      return slots.some((slot) => this.isByOrder ? this.isAvailableByOrder(fullDate, slot) : this.isAvailable(fullDate, slot));
    });
  }

  get firstAvailableLabel(): string | null {
    for (const date of this.dates) {
      const slots = this.availability[date.fullDate] ?? [];
      for (const slot of slots) {
        let availableHour: string | null = null;
        if (this.isByOrder) {
          const [start, end] = slot.split(' - ');
          if (start && end) availableHour = this.getFirstAvailableSlot(date.fullDate, start, end);
        } else if (this.isAvailable(date.fullDate, slot)) {
          availableHour = slot;
        }

        if (availableHour) return `${date.day}, ${date.date} às ${availableHour}`;
      }
    }

    return null;
  }

  private loadData(): void {

    this.encounterService
      .listByPractitionerNew(this.healthcareservice.practitioner_id)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: (encounters) => {
          this.processEncounters(encounters);
        },
        error: (err) => console.log('Erro ao buscar encounters:', err),
      });
  }

  private processEncounters(encounters: Encounter[]): void {
    this.encountersDone = {};
    (Array.isArray(encounters) ? encounters : []).forEach((encounter) => {
      const [date, hour] = String(encounter?.startDate ?? '').split(' ');
      if (!date || !hour || String(encounter?.status).toLowerCase() === 'cancelled') return;

      if (!this.encountersDone[date]) this.encountersDone[date] = [];
      this.encountersDone[date].push(hour);
    });
    this.encounterPractitioner = Array.isArray(encounters) ? encounters : [];
    this.refreshAvailability();
  }

  private isPreviousWeekAllowed(): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const proposedDate = new Date(this.currentDate);
    proposedDate.setDate(proposedDate.getDate() - this.getLimitDays());
    proposedDate.setHours(0, 0, 0, 0);


    return proposedDate >= today;
  }

  get canGoToPreviousWeek(): boolean {
    return this.isPreviousWeekAllowed();
  }

  goToPreviousWeek() {
    if (this.isAnimating || !this.isPreviousWeekAllowed()) return;

    this.animationDirection = 'left';
    this.isAnimating = true;

    this.previousWeek();

    setTimeout(() => {
      this.offset -= 4;
      this.animationDirection = null;
      this.isAnimating = false;
    }, 300);
  }

  goToNextWeek() {
    if (this.isAnimating) return;
    this.animationDirection = 'right';
    this.isAnimating = true;

    this.nextWeek();

    setTimeout(() => {
      this.offset += 4;
      this.animationDirection = null;
      this.isAnimating = false;
    }, 300);
  }


  getDisplayedHours(date: string): string[] {
    const slots = this.availability[date] || [];
    return this.expanded ? slots : slots.slice(0, this.initialSlotsCount);
  }

  toggleExpandAll(): void {
    this.expanded = !this.expanded;
  }

  hasMoreThanFiveSlots(): boolean {
    return Object.values(this.availability).some((slots) => slots.length > 5);
  }

  detectWhenIsByOrder(specialty: any) {
    this.isByOrder = specialty.byOrder === true;
  }

  getPractitionerHours(): void {
    const { organization_id, healthcareservice_id, practitioner_id } = this.healthcareservice ?? {};

    this.loading = true;
    this.hoursLoadMessage = null;
    this.canRetryHoursLoad = false;
    this.practitionerHours = [];
    this.availability = {};

    if (!organization_id || !healthcareservice_id || !practitioner_id) {
      this.hoursLoadMessage = 'Não foi possível identificar a agenda deste profissional para o procedimento.';
      this.loading = false;
      return;
    }

    let receivedResponse = false;

    this.affiliationOrganizationPractitioner
      .listByPractitionerOrganization(organization_id, practitioner_id)
      .pipe(
        timeout({ first: 15000 }),
        take(1),
        debounceTime(300),
        takeUntil(this.destroy$),
        finalize(() => {
          if (!receivedResponse && !this.hoursLoadMessage && !this.destroy$.isStopped) {
            this.hoursLoadMessage = 'A busca de horários foi concluída sem retornar uma agenda. Tente novamente.';
            this.canRetryHoursLoad = true;
          }
          this.loading = false;
        }),
      )
      .subscribe({
        next: (affiliation: any) => {
          receivedResponse = true;

          try {
            const affiliations = Array.isArray(affiliation)
              ? affiliation
              : affiliation && typeof affiliation === 'object'
                ? [affiliation]
                : [];
            const specialties = affiliations.flatMap((item: any) => (Array.isArray(item?.specialty) ? item.specialty : []));
            const specialty = specialties.find(
              (item: any) => String(item?.id) === String(healthcareservice_id) && item?.status === 'accepted'
            );

            if (!specialty) {
              this.hoursLoadMessage = 'A agenda deste procedimento ainda não está ativa nesta unidade.';
              return;
            }

            const interval = Number(specialty.averageDuration);
            this.averageDuration = Number.isFinite(interval) && interval > 0 ? interval : 30;
            this.practitionerHours = Array.isArray(specialty.practitionerHours) ? specialty.practitionerHours : [];
            this.isByOrder = specialty.byOrder === true;
            this.mapAvailabilityFromPractitionerHours(this.averageDuration);
          } catch (err) {
            console.error('Não foi possível interpretar os horários do especialista:', err);
            this.hoursLoadMessage = 'A agenda retornou dados inválidos. Tente carregar os horários novamente.';
            this.canRetryHoursLoad = true;
          }
        },
        error: (err) => {
          console.error('Não foi possível carregar os horários do especialista:', err);
          this.hoursLoadMessage = err?.name === 'TimeoutError'
            ? 'A busca de horários demorou mais de 15 segundos. Verifique sua conexão e tente novamente.'
            : 'Não foi possível carregar os horários agora. Verifique sua conexão e tente novamente.';
          this.canRetryHoursLoad = true;
        },
      });
  }

  private getPractitioner(): void {
    this.practitionerService
      .showNew(this.practitioner_id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (practitioner) => {
          this.practitioner = practitioner;
          this.refreshAvailability();
        },
        error: (err) => console.log(err),
      });
  }

  getOrganization(): void {
    this.organizationService
      .showNew(this.organization_id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (org) => {
          this.organization = org;
          this.refreshAvailability();
        },
        error: (err) => console.log(err),
      });
  }

  private refreshAvailability(): void {
    const modifiedDays = [
      ...(Array.isArray(this.practitioner?.modifiedDays) ? this.practitioner.modifiedDays : []),
      ...(Array.isArray(this.organization?.modifiedDays) ? this.organization.modifiedDays : []),
    ];
    this.blockedDates = Array.from(
      new Set(
        modifiedDays
          .filter((day) => day?.date)
          .map((day) => dayjs(day.date).tz('America/Sao_Paulo').format('YYYY-MM-DD'))
      )
    );

    if (Array.isArray(this.practitionerHours)) {
      this.mapAvailabilityFromPractitionerHours(this.averageDuration);
    }
  }

  getLimitDays() {
    return this.utilsService.getLimitDays();
  }

  generateWeek(baseDate: Date) {
    const dias = ['Domingo', 'Segunda', 'Terca', 'Quarta', 'Quinta', 'Sexta', 'Sabado'];
    this.dates = [];
    let limit = this.getLimitDays();

    for (let i = 0; i < limit; i++) {
      const d = new Date(baseDate);
      d.setDate(baseDate.getDate() + i);

      const day = dias[d.getDay()];
      const date = d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
      const fullDate = [
        d.getFullYear(),
        String(d.getMonth() + 1).padStart(2, '0'),
        String(d.getDate()).padStart(2, '0'),
      ].join('-');

      this.dates.push({ day, date, fullDate });
    }

  }

  mapAvailabilityFromPractitionerHours(interval: number = 30) {
    const normalizeDay = (day: string): string =>
      day.normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLocaleLowerCase('pt-BR');

    this.availability = {};

    for (const item of this.practitionerHours) {
      if (!item?.active || !Array.isArray(item.hours)) continue;

      const dateMatch = this.dates.find((d) => normalizeDay(d.day) === normalizeDay(String(item.dia ?? '')));

      if (!dateMatch) continue;

      const fulldate = dateMatch.fullDate;
      if (!this.availability[fulldate]) this.availability[fulldate] = [];

      if (this.blockedDates.includes(fulldate)) continue;

      for (const h of item.hours) {
        if (typeof h?.startHour !== 'string' || typeof h?.endHour !== 'string') continue;

        if (this.isByOrder) {
          const rangeLabel = `${h.startHour} - ${h.endHour}`;
          this.availability[fulldate].push(rangeLabel);
        } else {
          const slots = this.generateTimeSlots(h.startHour, h.endHour, interval).filter(
            (slot) => !this.encountersDone?.[fulldate]?.includes(slot) && this.isDateHourFuture(fulldate, slot)
          );

          this.availability[fulldate].push(...slots);
        }
      }

      this.availability[fulldate] = Array.from(new Set(this.availability[fulldate])).sort();
    }

  }

  generateTimeSlots(start: string, end: string, interval: number = 30): string[] {
    const slots: string[] = [];
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    const cur = new Date();
    cur.setHours(sh, sm, 0, 0);
    const endDate = new Date();
    endDate.setHours(eh, em, 0, 0);

    if (isNaN(cur.getTime()) || isNaN(endDate.getTime()) || interval <= 0) {
      console.error('Invalid time input or interval');
      return [];
    }

    while (cur < endDate) {
      const hh = String(cur.getHours()).padStart(2, '0');
      const mm = String(cur.getMinutes()).padStart(2, '0');
      slots.push(`${hh}:${mm}`);
      cur.setMinutes(cur.getMinutes() + interval);
    }
    return slots;
  }

  isAvailable(date: string, hour: string): boolean {
    const isSlotAvailable = this.availability[date]?.includes(hour);
    const isSlotTaken = this.encountersDone?.[date]?.includes(hour);

    if (!isSlotAvailable || isSlotTaken) return false;

    return this.isDateHourFuture(date, hour);
  }

  isDateHourFuture(date: string, hour: string) {
    const [hourPart, minutePart] = hour.split(':').map(Number);

    const [year, month, day] = date.split('-').map(Number);
    const slotDateTime = new Date(year, month - 1, day, hourPart, minutePart, 0);

    const now = new Date();

    return slotDateTime > now;
  }

  isAvailableByOrder(date: string, range: string): boolean {
    const [start, end] = range.split(' - ');

    const endDateTime = new Date(`${date}T${end}:00`);

    if (endDateTime < new Date()) {
      return false;
    }

    const selectedHour = this.getFirstAvailableSlot(date, start, end);

    return selectedHour !== null;
  }

  isSelected(date: string, hour: string): boolean {
    return this.selectedSlot?.date === date && this.selectedSlot?.hour === hour;
  }

  isSelectedByOrder(date: string, range: string): boolean {
    const [start, end] = range.split(' - ');
    const firstAvailableSlot = this.getFirstAvailableSlot(date, start, end);
    return firstAvailableSlot !== null && this.isSelected(date, firstAvailableSlot);
  }

  selectHour(date: string, hour: string): void {
    this.selectedSlot = { date, hour };
    const datetime = `${date} ${hour}`;

    this.onSelectedDatetime.emit({
      'datetime': datetime,
      'isByOrder': this.isByOrder
    });
  }

  selectHourByOrder(date: string, range: string) {
    const [start, end] = range.split(' - ');
    const selectedHour = this.getFirstAvailableSlot(date, start, end);
    if (!selectedHour) return;

    this.selectedSlot = { date, hour: selectedHour };

     this.onSelectedDatetime.emit({
      'datetime': `${date} ${selectedHour}`,
      'isByOrder': this.isByOrder,
      'starthour': `${date} ${start}`, 
    });

  }

  getFirstAvailableSlot(date: string, start: string, end: string): string | null {
    const slots = this.generateTimeSlots(start, end);

    const encountersDoneForDate = this.encountersDone[date] || [];

    const availableSlot = slots.find(
      (slot) => !encountersDoneForDate.includes(slot) && this.isDateHourFuture(date, slot)
    );

    return availableSlot || null;
  }

  previousWeek() {
    this.currentDate.setDate(this.currentDate.getDate() - this.getLimitDays());
    this.generateWeek(this.currentDate);
    this.mapAvailabilityFromPractitionerHours(this.averageDuration);
  }

  nextWeek() {
    this.currentDate.setDate(this.currentDate.getDate() + this.getLimitDays());
    this.generateWeek(this.currentDate);
    this.mapAvailabilityFromPractitionerHours(this.averageDuration);
  }

  trackByDate(index: number, item: any): string {
    return item.fullDate;
  }

  trackByRange(index: number, item: string): string {
    return item;
  }

  trackByHour(index: number, item: string): string {
    return item;
  }
}
